import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { postsService } from '../../services/posts.service';
import { Post } from '../../types';
import { isVideoUrl } from '../../utils/media';
import Combobox from '../Combobox';
import './CreatePostModal.css';

interface EditPostModalProps {
  isOpen: boolean;
  post: Post | null;
  onClose: () => void;
  onPostUpdated: () => void;
}

const EditPostModal: React.FC<EditPostModalProps> = ({
  isOpen,
  post,
  onClose,
  onPostUpdated,
}) => {
  const [caption, setCaption] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<'POST' | 'ANNOUNCEMENT' | 'POLICY'>('POST');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const mapPostCategoryToForm = (p: Post): 'POST' | 'ANNOUNCEMENT' | 'POLICY' => {
    if (p.category === 'NEWS') return 'POST';
    if (p.category === 'SPOTLIGHT_EOM' || p.category === 'SPOTLIGHT_NEW_EMPLOYEE') return 'ANNOUNCEMENT';
    return p.category as 'POST' | 'ANNOUNCEMENT' | 'POLICY';
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Populate form when post changes
  useEffect(() => {
    if (post) {
      setCaption(post.caption);
      setContent(post.content || '');
      setCategory(mapPostCategoryToForm(post));
    }
  }, [post]);

  if (!isOpen || !post) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!caption.trim()) {
      setError('Caption is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const categoryToSave =
        post.category === 'SPOTLIGHT_EOM' || post.category === 'SPOTLIGHT_NEW_EMPLOYEE'
          ? post.category
          : category;
      // Only text fields — never send department/accessScope/branch; those define audience
      // and must not be overwritten from this modal (was causing company posts to "disappear").
      await postsService.update(post.id, {
        caption: caption.trim(),
        content: content.trim() || undefined,
        category: categoryToSave,
      });

      onPostUpdated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content post-form-modal">
        <div className="modal-header">
          <h2>Edit Post</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form className="post-form-modal__form" onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="edit-caption">Title *</label>
              <input
                type="text"
                id="edit-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter post title..."
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-content">Content</label>
              <textarea
                id="edit-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your post content..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-category">Category</label>
              <Combobox
                id="edit-category"
                ariaLabel="Category"
                value={category}
                onChange={(v) => setCategory(v as 'POST' | 'ANNOUNCEMENT' | 'POLICY')}
                disabled={
                  post.category === 'SPOTLIGHT_EOM' || post.category === 'SPOTLIGHT_NEW_EMPLOYEE'
                }
                options={[
                  { value: 'POST', label: 'Post' },
                  { value: 'ANNOUNCEMENT', label: 'Announcement' },
                  { value: 'POLICY', label: 'Policy' },
                ]}
              />
              {(post.category === 'SPOTLIGHT_EOM' || post.category === 'SPOTLIGHT_NEW_EMPLOYEE') && (
                <span className="field-note">Type is managed from the right sidebar spotlight editor.</span>
              )}
            </div>

            {post.cdnLink && (
              <div className="form-group">
                <label>Current media</label>
                <div className="current-image-preview">
                  {isVideoUrl(post.cdnLink) ? (
                    <video src={post.cdnLink} controls muted playsInline />
                  ) : (
                    <img src={post.cdnLink} alt="Current" />
                  )}
                </div>
                <span className="field-note">Image or video cannot be changed during edit</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditPostModal;
